import { useState, useEffect, useCallback } from 'react';
import {
  listActiveSavedRemarks,
  listActiveSavedRemarkFiles,
  getSavedRemarkFile,
  SAVED_REMARK_FILES_BUCKET,
} from '../api/savedRemarks';
import { createSignedStorageUrl, removeStorageObjects, uploadStorageObject } from '../api/storage';
import {
  insertSavedRemark,
  updateSavedRemark,
  deactivateSavedRemark,
  insertSavedRemarkFile,
  deactivateSavedRemarkFile,
  trackSavedRemarkUsage,
} from '../services/savedRemarks';
import {
  SavedRemark, 
  SavedRemarkRow, 
  SavedRemarkInsert, 
  SavedRemarkUpdate, 
  SavedRemarksHookResult,
  SavedRemarkFile,
  SavedRemarkFileRow,
  SavedRemarkFileInsert
} from '../types/savedRemarks';

// Helper functions to transform database rows to frontend types
const transformFile = (row: SavedRemarkFileRow): SavedRemarkFile => ({
  id: row.id,
  savedRemarkId: row.saved_remark_id,
  filename: row.filename,
  originalFilename: row.original_filename,
  fileSize: row.file_size,
  mimeType: row.mime_type || undefined,
  storagePath: row.storage_path,
  storageBucket: row.storage_bucket || undefined,
  createdAt: row.created_at ? new Date(row.created_at) : new Date(),
  updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
  createdBy: row.created_by || undefined,
  isActive: row.is_active !== false, // Default to true if null
});

const transformRemark = (row: SavedRemarkRow, files?: SavedRemarkFileRow[]): SavedRemark => ({
  id: row.id,
  title: row.title,
  content: row.content,
  price: row.price || undefined,
  createdAt: row.created_at ? new Date(row.created_at) : new Date(),
  updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
  usageCount: row.usage_count || 0,
  createdBy: row.created_by || undefined,
  isActive: row.is_active !== false, // Default to true if null
  files: files ? files.filter(f => f.is_active !== false).map(transformFile) : undefined,
});

export function useSavedRemarks(): SavedRemarksHookResult {
  const [remarks, setRemarks] = useState<SavedRemark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch remarks with files from Supabase
  const fetchRemarks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch remarks
      const { data: remarksData, error: remarksError } = await listActiveSavedRemarks();

      if (remarksError) {
        console.error('Error fetching saved remarks:', remarksError);
        setError('Failed to load saved remarks');
        return;
      }

      // Fetch files for all remarks
      const { data: filesData, error: filesError } = await listActiveSavedRemarkFiles();

      if (filesError) {
        console.error('Error fetching saved remark files:', filesError);
        // Don't fail completely if files can't be loaded, just log the error
      }

      // Group files by remark ID
      const filesByRemarkId = (filesData || []).reduce((acc, file) => {
        if (!acc[file.saved_remark_id]) {
          acc[file.saved_remark_id] = [];
        }
        acc[file.saved_remark_id].push(file);
        return acc;
      }, {} as Record<number, SavedRemarkFileRow[]>);

      // Transform remarks with their associated files
      const transformedRemarks = (remarksData || []).map(remarkRow => 
        transformRemark(remarkRow, filesByRemarkId[remarkRow.id])
      );
      
      setRemarks(transformedRemarks);
    } catch (err) {
      console.error('Unexpected error fetching remarks:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load remarks on mount
  useEffect(() => {
    fetchRemarks();
  }, [fetchRemarks]);

  // File management functions (defined before addRemark to avoid circular dependency)
  const addFilesToRemark = useCallback(async (remarkId: number, files: File[]): Promise<SavedRemarkFile[]> => {
    const uploadedFiles: SavedRemarkFile[] = [];
    const STORAGE_BUCKET = SAVED_REMARK_FILES_BUCKET;

    for (const file of files) {
      try {
        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `remark_${remarkId}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await uploadStorageObject(STORAGE_BUCKET, filePath, file);

        if (uploadError) {
          console.error('Error uploading file:', {
            message: uploadError.message,
            name: uploadError.name,
            error: uploadError,
            bucket: STORAGE_BUCKET,
            path: filePath,
            fileType: file.type,
            fileSize: file.size
          });
          continue;
        }

        // Create file record in database
        const fileInsert: SavedRemarkFileInsert = {
          saved_remark_id: remarkId,
          filename: fileName,
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type || null,
          storage_path: filePath,
          storage_bucket: STORAGE_BUCKET,
          created_by: null, // TODO: Add user management later
        };

        const { data: fileData, error: fileError } = await insertSavedRemarkFile(fileInsert);

        if (fileError || !fileData) {
          console.error('Error creating file record:', fileError);
          // Clean up uploaded file
          await removeStorageObjects(STORAGE_BUCKET, [filePath]);
          continue;
        }

        uploadedFiles.push(transformFile(fileData));
      } catch (error) {
        console.error('Error processing file:', file.name, error);
      }
    }

    return uploadedFiles;
  }, []);

  // Add a new remark with optional files
  const addRemark = useCallback(async (title: string, content: string, price?: number, files?: File[]): Promise<SavedRemark | null> => {
    try {
      setError(null);

      const insertData: SavedRemarkInsert = {
        title: title.trim(),
        content: content.trim(),
        price: price || null,
        created_by: null, // TODO: Add user management later
      };

      const { data, error: supabaseError } = await insertSavedRemark(insertData);

      if (supabaseError || !data) {
        console.error('Error adding remark:', supabaseError);
        setError('Failed to save remark');
        return null;
      }

      let uploadedFiles: SavedRemarkFile[] = [];
      
      // Upload files if provided
      if (files && files.length > 0) {
        try {
          uploadedFiles = await addFilesToRemark(data.id, files);
        } catch (fileError) {
          console.error('Error uploading files:', fileError);
          // Don't fail the remark creation if file upload fails
        }
      }

      const newRemark = transformRemark(data, uploadedFiles.map(f => ({
        id: f.id,
        saved_remark_id: f.savedRemarkId,
        filename: f.filename,
        original_filename: f.originalFilename,
        file_size: f.fileSize,
        mime_type: f.mimeType || null,
        storage_path: f.storagePath,
        storage_bucket: f.storageBucket || null,
        created_at: f.createdAt.toISOString(),
        updated_at: f.updatedAt.toISOString(),
        created_by: f.createdBy || null,
        is_active: f.isActive
      })));
      
      setRemarks(prevRemarks => [newRemark, ...prevRemarks]);
      return newRemark;
    } catch (err) {
      console.error('Unexpected error adding remark:', err);
      setError('An unexpected error occurred');
      return null;
    }
  }, [addFilesToRemark]);

  // Update an existing remark
  const updateRemark = useCallback(async (
    id: number, 
    updates: { title?: string; content?: string; price?: number | null }
  ): Promise<boolean> => {
    try {
      setError(null);

      const updateData: SavedRemarkUpdate = {};
      if (updates.title !== undefined) updateData.title = updates.title.trim();
      if (updates.content !== undefined) updateData.content = updates.content.trim();
      // null clears the price; undefined leaves it untouched
      if (updates.price !== undefined) updateData.price = updates.price || null;

      const { data, error: supabaseError } = await updateSavedRemark(id, updateData);

      if (supabaseError || !data) {
        console.error('Error updating remark:', supabaseError);
        setError('Failed to update remark');
        return false;
      }

      // The row carries no files; keep the ones already loaded for it
      const updatedRemark = transformRemark(data);
      setRemarks(prevRemarks =>
        prevRemarks.map(remark => remark.id === id ? { ...updatedRemark, files: remark.files } : remark)
      );
      return true;
    } catch (err) {
      console.error('Unexpected error updating remark:', err);
      setError('An unexpected error occurred');
      return false;
    }
  }, []);

  // Delete a remark (soft delete by setting is_active to false)
  const deleteRemark = useCallback(async (id: number): Promise<boolean> => {
    try {
      setError(null);

      const { error: supabaseError } = await deactivateSavedRemark(id);

      if (supabaseError) {
        console.error('Error deleting remark:', supabaseError);
        setError('Failed to delete remark');
        return false;
      }

      setRemarks(prevRemarks => prevRemarks.filter(remark => remark.id !== id));
      return true;
    } catch (err) {
      console.error('Unexpected error deleting remark:', err);
      setError('An unexpected error occurred');
      return false;
    }
  }, []);

  // Increment usage count
  const incrementUsageCount = useCallback(async (id: number): Promise<boolean> => {
    try {
      setError(null);

      // Only a remark this list has loaded is counted
      const currentRemark = remarks.find(r => r.id === id);
      if (!currentRemark) return false;

      // The backend adds one to the stored count and answers with the row
      const { data, error: supabaseError } = await trackSavedRemarkUsage(id);

      if (supabaseError || !data) {
        console.error('Error incrementing usage count:', supabaseError);
        setError('Failed to update usage count');
        return false;
      }

      // The row carries no files; keep the ones already loaded for it
      const updatedRemark = transformRemark(data);
      setRemarks(prevRemarks =>
        prevRemarks.map(remark => remark.id === id ? { ...updatedRemark, files: remark.files } : remark)
      );
      return true;
    } catch (err) {
      console.error('Unexpected error incrementing usage count:', err);
      setError('An unexpected error occurred');
      return false;
    }
  }, [remarks]);

  // Search remarks (client-side filtering)
  const searchRemarks = useCallback((query: string): SavedRemark[] => {
    if (!query.trim()) return remarks;

    const lowercaseQuery = query.toLowerCase();
    return remarks.filter(remark => 
      remark.title.toLowerCase().includes(lowercaseQuery) ||
      remark.content.toLowerCase().includes(lowercaseQuery) ||
      (remark.price && remark.price.toString().includes(query.trim()))
    );
  }, [remarks]);

  // Get sorted remarks
  const getSortedRemarks = useCallback((sortBy: 'recent' | 'usage' | 'alphabetical' = 'recent'): SavedRemark[] => {
    const sortedRemarks = [...remarks];
    
    switch (sortBy) {
      case 'usage':
        return sortedRemarks.sort((a, b) => b.usageCount - a.usageCount);
      case 'alphabetical':
        return sortedRemarks.sort((a, b) => a.title.localeCompare(b.title));
      case 'recent':
      default:
        return sortedRemarks.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    }
  }, [remarks]);

  // Additional file management functions
  const deleteFileFromRemark = useCallback(async (fileId: number): Promise<boolean> => {
    try {
      setError(null);

      // Get file info first
      const { data: fileData, error: fetchError } = await getSavedRemarkFile(fileId);

      if (fetchError) {
        console.error('Error fetching file:', fetchError);
        return false;
      }

      // Delete from storage
      const { error: storageError } = await removeStorageObjects(
        fileData.storage_bucket || SAVED_REMARK_FILES_BUCKET,
        [fileData.storage_path],
      );

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
      }

      // Soft delete file record
      const { error: dbError } = await deactivateSavedRemarkFile(fileId);

      if (dbError) {
        console.error('Error deleting file record:', dbError);
        setError('Failed to delete file');
        return false;
      }

      // Update local state
      setRemarks(prevRemarks =>
        prevRemarks.map(remark => ({
          ...remark,
          files: remark.files?.filter(f => f.id !== fileId)
        }))
      );

      return true;
    } catch (err) {
      console.error('Unexpected error deleting file:', err);
      setError('An unexpected error occurred');
      return false;
    }
  }, []);

  const getFileDownloadUrl = useCallback(async (file: SavedRemarkFile): Promise<string | null> => {
    try {
      const { data, error } = await createSignedStorageUrl(
        file.storageBucket || SAVED_REMARK_FILES_BUCKET,
        file.storagePath,
        3600, // 1 hour expiry
      );

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (err) {
      console.error('Error getting download URL:', err);
      return null;
    }
  }, []);

  // Refetch remarks
  const refetchRemarks = useCallback(async () => {
    await fetchRemarks();
  }, [fetchRemarks]);

  return {
    remarks,
    loading,
    error,
    addRemark,
    updateRemark,
    deleteRemark,
    incrementUsageCount,
    searchRemarks,
    getSortedRemarks,
    refetchRemarks,
    addFilesToRemark,
    deleteFileFromRemark,
    getFileDownloadUrl,
    totalRemarks: remarks.length,
  };
}
